import { Fragment, useContext, useState } from "react";
import { Card, CardBody, CardHeader } from "@/components/bootstrap/TemporaryReactstrapCompatibilityLayer";
import AuthContext from "../../contexts/auth/AuthContext";
import Button from "../layout/Button";
import Loading from "../ui-elements/Loading";
import ConfirmDeleteModal from "../management/seedTypes/ConfirmDeleteModal";

const TenantEditor = ({ tenant, saveHandler, deleteHandler }) => {
  const { currentTenant } = useContext(AuthContext);

  const initialState = {
    name: {
      value: tenant?.name ?? "",
      touched: false,
      validate: (text) => text.length >= 3,
      isValid: !!tenant?.name,
      placeholder: "Tenant name ...",
    },
    tenantId: {
      value: tenant?.id ?? "",
      touched: false,
      validate: () => true,
      isValid: true,
      placeholder: "",
    },
  };

  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tenantState, setTenantState] = useState(initialState);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const isStateValid = Object.values(tenantState)
    .map((entry) => entry.isValid)
    .every(Boolean);

  const isStateTouched = Object.values(tenantState).some((e) => !!e.touched);

  const activateEditState = () => setEdit(true);

  const createRequestBody = () => {
    const updatedTenantProperties = Object.entries(tenantState).reduce(
      (acc, [key, valueObject]) => ({ ...acc, [key]: valueObject.value }),
      {}
    );
    return { ...tenant, ...updatedTenantProperties };
  };

  const handleDelete = () => setShowConfirmDelete(true);

  const confirmDelete = async () => {
    setLoading(true);
    try {
      const newTenant = createRequestBody(tenant);
      await deleteHandler(newTenant);
      setEdit(false);
    } finally {
      setLoading(false);
      setShowConfirmDelete(false);
    }
  };

  const handleSave = () => {
    if (!tenant?.id || !isStateValid || !isStateTouched) return;
    const body = createRequestBody();
    saveHandler(body).then(() => setEdit(false));
  };

  const onChange = (event) => {
    event.persist();
    const key = event.currentTarget.name;
    const value = event.currentTarget.value;
    setTenantState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
        isValid: prev[key].validate(value),
        touched: true,
      },
    }));
  };

  const reset = () => {
    setEdit(false);
    setTenantState(initialState);
  };

  const isCurrent = tenant?.id === currentTenant?.id;

  return loading ? (
    <Loading fullScreen />
  ) : (
    <>
      <ConfirmDeleteModal
        show={showConfirmDelete}
        onCancel={() => setShowConfirmDelete(false)}
        onConfirm={confirmDelete}
        header="DELETE TENANT"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        message={
          <>
            When you delete a tenant, all related data (users, containers,
            plants, produce, reports, pictures, weights, and more) will be
            permanently removed.
            <br />
            This action cannot be undone.
          </>
        }
      />

      <Card className={`tenant-card ${edit ? "is-editing" : ""}`}>
        <CardHeader className="tenant-card-header">
          <input
            className={
              tenantState.name.isValid || !tenantState.name.touched
                ? "h4-input tenant-title-input"
                : "h4-input tenant-title-input invalid-field"
            }
            type="text"
            minLength="3"
            value={tenantState.name.value}
            onChange={onChange}
            name="name"
            id={`tenant-name-${tenant?.id ?? "new"}`}
            placeholder={tenantState.name.placeholder}
            disabled={edit === false}
          />
        </CardHeader>

        <CardBody className="tenant-card-body">
          <Fragment>
            {!tenantState.name.isValid && tenantState.name.touched && (
              <p className="tenant-validation">Name must have 3 characters or more.</p>
            )}
          </Fragment>
        </CardBody>

        <div className="card-button tenant-card-footer">
          {edit ? (
            <Fragment>
              <Button className="tenant-card-btn" label="Cancel" clickHandler={reset} />
              <Button
                className="tenant-card-btn"
                label="Save"
                clickHandler={handleSave}
                disabled={!isStateValid || !isStateTouched}
              />
            </Fragment>
          ) : (
            <Fragment>
              <Button
                className="tenant-card-btn"
                label="Delete"
                clickHandler={handleDelete}
                disabled={isCurrent}
              />
              <Button
                className="tenant-card-btn"
                label="Edit"
                clickHandler={activateEditState}
              />
            </Fragment>
          )}
        </div>
      </Card>
    </>
  );
};

export default TenantEditor;
